import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './index.css';
import * as Sentry from '@sentry/react';
import { assertWebEnvironment } from './config/web-environment.guard';
import { captureReferralFromUrl } from './app/utils/referralAttribution';
import { exchangeCrossSiteSsoCodeFromUrl } from './app/utils/crossSiteSso';
import { refreshPlatformSiteMarkers } from './app/utils/platformSiteMarkers';
import { applyBrandTheme } from './app/utils/brandTheme';
import { getBrand } from './app/constants/brands';
import { sentryBrandOptions } from './app/utils/sentryBrandOptions';
import { applyBrandDefaultEnvironment } from './config/brandDefaultEnvironment';

assertWebEnvironment();

// Brand identity (colors, fonts, title, favicon) before React renders.
const brand = getBrand();
applyBrandTheme(brand);

// Capture ?referrer= marker before React renders (stores 30 days in localStorage)
captureReferralFromUrl();
void refreshPlatformSiteMarkers();
void exchangeCrossSiteSsoCodeFromUrl();

// Initialize Sentry before rendering React (W12 / 8.3: Lenvoy skips Replay + default PII)
const sentryBrand = sentryBrandOptions(brand);
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'development',
  integrations: [
    Sentry.browserTracingIntegration(),
    ...(sentryBrand.enableSessionReplay ? [Sentry.replayIntegration()] : []),
  ],
  tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0, // 10% in production, 100% in development
  replaysSessionSampleRate: sentryBrand.replaysSessionSampleRate,
  replaysOnErrorSampleRate: sentryBrand.replaysOnErrorSampleRate,
  sendDefaultPii: sentryBrand.sendDefaultPii,
});
Sentry.setTag('brandSite', sentryBrand.brandSite);

function startApp() {
  const container = document.getElementById('root')!;
  const root = createRoot(container);

  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

// Spec 027: while the platform owner sets this brand's default environment to Testing, its
// production website sends visitors to the brand's testing site. Nothing to wait for (and no
// request) on any other host or when the brand has no testing site; a failure just renders.
applyBrandDefaultEnvironment(brand).then(
  redirecting => {
    if (!redirecting) startApp();
  },
  () => startApp(),
);

// If you want to start measuring performance in your app, pass a function
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
